<template>
  <div v-if="congress.id">
    <b-container fluid>
      <b-col
        cols="12"
        lg="7"
        xl="5"
        class="mx-auto"
      >
        <b-img
          v-if="congress.bgBanner"
          :src="congress.bgBanner"
          fluid
          alt="Congress Banner"
          class="w-100 mb-4"
        />
      </b-col>
    </b-container>
    <b-container fluid>
      <b-row class="mb-4">
        <b-col
          cols="12"
          lg="8"
          class="d-flex"
        >
          <congress-info-card
            :congress="congress"
            cta-route="congress-view"
            cta-label="Read More"
            class="flex-fill d-flex flex-column"
          />
        </b-col>
        <b-col
          v-if="congress.adaudienceId"
          cols="12"
          lg="4"
        >
          <b-card :title="$t('Attendance Stats')">
            <b-card-text>
              <div
                v-for="item in attendanceItems"
                :key="item.title"
                class="d-flex align-items-start justify-content-between"
              >
                <h5>
                  {{ $t(item.title) }}<br />
                  <small class="d-inline-block text-body">{{ $t(item.subtitle) }}</small>
                </h5>
                <strong>
                  {{ item.count }}
                </strong>
              </div>
              <b-button
                v-if="!!attendance.id"
                :href="altApi.replace(
                  '/api/',
                  `/certificate?booking_id=${attendance.id}&congress_id=${attendance.congress_id}&type_id=${attendance.type_id}`,
                )"
                variant="primary"
                target="_blank"
                class="mt-2"
                download
                block
              >
                <feather-icon
                  end
                  icon="DownloadCloudIcon"
                  class="mr-1"
                />
                {{ $t('Download Certificate') }}
              </b-button>
            </b-card-text>
          </b-card>
        </b-col>
      </b-row>
      <div v-if="abstracts.length > 0">
        <h1 class="mt-3 pb-2 d-flex justify-content-center align-items-center">
          <feather-icon
            icon="FileTextIcon"
            size="28"
            class="mr-1"
          />
          {{ $t('Abstracts') }}
        </h1>
        <b-row class="mb-4">
          <b-col
            v-for="abstract in abstracts"
            :key="abstract.id"
            cols="12"
            lg="12"
            class="d-flex"
          >
            <b-card
              :title="abstract.title"
              :sub-title="abstract.topic"
              class="flex-fill d-flex flex-column"
            >
              <b-card-text class="flex-grow-1">
                <small v-html="abstract.excerpt" />
              </b-card-text>
              <template #footer>
                <div class="d-flex justify-content-between align-items-center">
                  <span
                    v-if="abstract.code"
                    class="badge badge-secondary"
                  >
                    {{ abstract.code }}
                  </span>
                  <b-button
                    variant="outline-primary"
                    :href="abstract.view_link"
                    size="sm"
                    target="_blank"
                  >
                    {{ $t('Read More') }}
                  </b-button>
                </div>
              </template>
            </b-card>
          </b-col>
        </b-row>
      </div>
      <div v-if="sessions.length > 0">
        <h1 class="mt-3 pb-2 d-flex justify-content-center align-items-center">
          <feather-icon
            icon="CalendarIcon"
            size="28"
            class="mr-1"
          />
          {{ $t('Sessions') }}
        </h1>
        <b-row class="mb-4">
          <b-col
            v-for="item in sessions"
            :key="item.id"
            cols="12"
            lg="6"
            class="d-flex"
          >
            <b-card
              :style="'background-color:' + (item.color ? item.color : '#283046')"
              class="flex-fill d-flex flex-column text-white"
            >
              <b-card-title class="text-white">
                {{ item.title }}
              </b-card-title>
              <b-card-sub-title class="text-white">
                <span>{{ shortDate(item.start) }}</span> <span>{{ shortTime(item.start) }}</span>
                <span v-if="item.end">
                  <span> - </span>
                  <span>{{ shortTime(item.end) }}</span>
                </span>
              </b-card-sub-title>
              <b-card-text>
                <hr />
                <div class="d-flex justify-content-between align-items-center">
                  <h5
                    v-if="item.room"
                    class="room text-h5"
                    :class="item.hColor"
                  >
                    {{ $t('Congress_Room') }} {{ item.room }}
                  </h5>
                  <h4
                    v-if="item.session_type"
                    class="lecture_type text-h5 font-weight-bold"
                    :class="item.hColor"
                  >
                    {{ item.session_type }}
                  </h4>
                </div>
                <h3
                  v-if="item.type"
                  class="lecture_type text-h4"
                  :class="item.hColor"
                >
                  {{ item.type }}
                </h3>
                <template v-for="type in chairTypes">
                  <div
                    v-if="item[type]"
                    :key="type"
                    :class="type"
                  >
                    <h4
                      class="text-h5 mt-3"
                      :class="item.hColor"
                    >
                      {{ $t(type + (item[type].length > 0 ? '' : '_single')) }}
                    </h4>
                    <strong v-html="item[type].replace(/,/g, '<br />')" />
                  </div>
                </template>
                <div
                  v-if="item.speaches"
                  class="mt-4"
                >
                  <div
                    v-for="(speach, i) in item.speaches"
                    :key="speach.speaker"
                    :class="i > 0 ? 'mt-2' : ''"
                  >
                    <strong>{{ speach.speaker }}</strong>
                    <span
                      v-if="speach.time"
                      class="d-block"
                    >{{ speach.time }}</span>
                    <span class="d-block">{{ speach.title }}</span>
                  </div>
                </div>
                <div
                  v-if="item.sponsor"
                  class="mt-4"
                >
                  {{ $t('Organized by') }} {{ item.sponsor }}
                </div>
                <div
                  v-if="item.note"
                  class="mt-4"
                >
                  {{ item.note }}
                </div>
              </b-card-text>
            </b-card>
          </b-col>
        </b-row>
      </div>
    </b-container>
  </div>
</template>

<script>
import { BContainer, BRow, BCol } from 'bootstrap-vue'
import CongressInfoCard from './CongressInfoCard.vue'
import axios from 'axios'

export default {
  components: {
    BContainer,
    BRow,
    BCol,
    CongressInfoCard,
  },
  data() {
    return {
      attendance: {},
      adToken: null,
      congress: {},
      sessions: [],
      abstracts: [],
    }
  },
  computed: {
    userData() {
      return this.$store.getters['app-common/userData']
    },
    altApi() {
      return 'https://www.adaudience.gr/api/'
    },
    chairTypes() {
      return ['chairs', 'co_chairs', 'moderators', 'commentators', 'panelists']
    },
    attendanceItems() {
      return [
        {
          title: 'Attendance Duration',
          subtitle: 'in minutes',
          count: this.attendance.attendance_hours || 0,
        },
        {
          title: 'Points',
          subtitle: 'total',
          count: this.attendance.points || 0,
        },
      ].map(field => ({
        ...field,
        color: this.attendance.points ? 'success' : 'secondary',
      }))
    },
  },
  async mounted() {
    this.congress = (await this.$store.dispatch('app-common/fetchCongress', this.$route.params.id)).data
    if (this.congress.adaudienceId) {
      this.adToken = (await axios.post(`${this.altApi}token`, {
        key: '77547f82f24ad61d9e5db446839967c9d2e0'
      })).data.token
      const headers = { Authorization: `Bearer ${this.adToken}` }
      const params = { id: 0, congress: this.congress.adaudienceId, email: this.userData.email }
      const attendanceResp = (await axios.get(`${this.altApi}attendance`, { headers, params })).data
      if (attendanceResp.data) {
        this.attendance = attendanceResp.data
      }
      const sessionsResp = (await axios.get(`${this.altApi}person-sessions`, { headers, params })).data
      if (sessionsResp.data) {
        this.sessions = sessionsResp.data.map(s => {
          s.hColor = s.color ? '' : 'text-white'
          if (s.speakers) {
            s.speaches = []
            s.speakers.split('##').forEach(speaker => {
              const parts = speaker.split('--')
              const speach = { speaker: parts[0] }
              if (parts[2]) {
                speach.time = parts[1]
                speach.title = parts[2]
              } else {
                speach.title = parts[1]
              }
              s.speaches.push(speach)
            })
            s.speakerNames = s.speaches.map(sp => sp.speaker)
          }
          return s
        })
      }
      const abstractsResp = (await axios.get(`${this.altApi}person-abstracts`, { headers, params })).data
      if (abstractsResp.data) {
        this.abstracts = abstractsResp.data
      }
    }
  },
}
</script>
